/**
 * The other chef's voice, when the other chef is a program.
 *
 * The human's side of a human-AI round is meant to differ from a human-human
 * round in exactly one way: who is on the other side. Everything else is held
 * constant on purpose — the same mic check, the same latency gate, the same
 * tutorial, the same kitchen, the same content hash. So the modality is held too:
 * the player speaks out loud, and what comes back arrives in their ears.
 *
 * It arrives through `speechSynthesis`, which is in every browser, costs nothing,
 * sends no audio anywhere and needs no key. A paid voice is only worth buying if
 * a pilot shows the robot voice changes how people talk to it — and that is a
 * finding, not a purchase to make in advance.
 *
 * Two rules the rest of this file exists to keep.
 *
 * **The tape is the authority, not this module.** What is read aloud is the text
 * of the `UTTERANCE` event, unmodified. The shaping — markup stripped, length
 * capped — happened on the agent's side before the event was emitted
 * (`agents/src/tts.ts`), so the words on the tape are the words the human heard.
 * A browser that trimmed a line to fit would silently make the recorded
 * transcript a transcript of a different conversation.
 *
 * **Only the other seat, only `tts`.** An `UTTERANCE` from this player is this
 * player's own words coming back off the tape, and reading them aloud would be
 * the game talking to itself. A `text`-channel line belongs to an AI-AI round,
 * which has no listener; speaking one would put audio into a condition that is
 * defined by having none.
 */

import type { GameEvent, PlayerId } from '@overcooked/shared'

/**
 * The slice of the Web Speech API this needs, and no more.
 *
 * Narrowed to an interface so the decisions below can be tested in Node, where
 * there is no `speechSynthesis` at all. The alternative — a jsdom-shaped mock of
 * the real global — would be a test of the mock.
 */
export interface SpeechLike {
  speak(utterance: SpeechSynthesisUtterance): void
  cancel(): void
  getVoices(): SpeechSynthesisVoice[]
  readonly speaking: boolean
}

/** True when this browser can say anything at all. */
export function speechSupported(win: { speechSynthesis?: unknown } = window): boolean {
  return typeof win.speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined'
}

/**
 * Which voice to use.
 *
 * Preferences in order, and each one is there for a reason a player would
 * notice. A local voice, because a network voice stalls for a second on the
 * first line of the round and the player hears the pause as the agent
 * hesitating. English, because the prompt and the kitchen are in English. Then
 * the platform default, which on macOS and on Chrome is a voice a person can
 * follow at speed.
 *
 * `null` is a legitimate answer: it means "let the platform choose", which is
 * what an empty voice list on the first call means anyway — Chrome populates
 * `getVoices()` asynchronously.
 */
export function pickVoice(voices: SpeechSynthesisVoice[], preferred?: string): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null
  if (preferred) {
    const named = voices.find((v) => v.name === preferred)
    if (named) return named
  }
  const english = voices.filter((v) => v.lang?.toLowerCase().startsWith('en'))
  const pool = english.length > 0 ? english : voices
  return pool.find((v) => v.localService) ?? pool.find((v) => v.default) ?? pool[0] ?? null
}

/**
 * Whether this event is a line for this player to hear.
 *
 * The one decision that keeps the condition honest, so it is a named function
 * with tests rather than a condition inside an effect.
 */
export function shouldSpeak(event: GameEvent, selfId: PlayerId | null): boolean {
  if (event.type !== 'UTTERANCE') return false
  if (event.channel !== 'tts') return false
  if (selfId !== null && event.playerId === selfId) return false
  return event.text.trim().length > 0
}

export interface AgentVoiceOptions {
  synth?: SpeechLike | null
  /** Built by the caller so a test needs no `SpeechSynthesisUtterance` global. */
  makeUtterance?: (text: string) => SpeechSynthesisUtterance
  /**
   * Slightly quick on purpose.
   *
   * A kitchen changes every couple of seconds and the agent's lines are about
   * the kitchen. 1.05 is the fastest rate that is not audibly hurried on the
   * default platform voices.
   */
  rate?: number
  volume?: number
  voiceName?: string
  onSpoken?: (text: string) => void
}

export interface AgentVoice {
  /** Read a line aloud. The queue is the platform's. */
  say(text: string): void
  /** Drop anything queued or in progress — the round ended, or the tab is going. */
  silence(): void
  /** True when the browser has no speech at all, so the UI can say so. */
  readonly mute: boolean
  readonly spoken: number
}

/**
 * A voice for the agent seat.
 *
 * Deliberately not a React hook. It is used from inside a frame handler and from
 * an effect's teardown, and a hook would make the queue a thing that gets
 * rebuilt when a parent re-renders — which is how a line gets read twice.
 */
export function createAgentVoice(options: AgentVoiceOptions = {}): AgentVoice {
  const synth =
    options.synth !== undefined
      ? options.synth
      : typeof window !== 'undefined' && speechSupported()
        ? (window.speechSynthesis as SpeechLike)
        : null

  const make =
    options.makeUtterance ??
    ((text: string) => new SpeechSynthesisUtterance(text))

  let spoken = 0
  /**
   * Resolved on the first line rather than up front.
   *
   * Chrome fills `getVoices()` asynchronously and returns an empty array
   * immediately after load, so a voice chosen in a constructor is no voice at
   * all — on a page that has just opened, which is every page here.
   */
  let voice: SpeechSynthesisVoice | null = null

  return {
    get mute() {
      return synth === null
    },
    get spoken() {
      return spoken
    },
    say(text: string) {
      if (!synth) return
      const line = text.trim()
      if (!line) return
      if (!voice) voice = pickVoice(synth.getVoices(), options.voiceName)
      const utterance = make(line)
      if (voice) utterance.voice = voice
      utterance.rate = options.rate ?? 1.05
      utterance.volume = options.volume ?? 1
      synth.speak(utterance)
      spoken += 1
      options.onSpoken?.(line)
    },
    silence() {
      synth?.cancel()
    },
  }
}
